import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Calendar, Clock, Star, Flame } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DatePicker from '../components/DatePicker';
import { useLanguage } from '../i18n/LanguageContext';
import { ALL_TIME_SLOTS, endTimesFor, formatTime12, hoursBetween } from '../utils/timeSlots';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [zipCode, setZipCode] = useState('');
  const [serviceDate, setServiceDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [surge, setSurge] = useState(null);
  const [platformSettings, setPlatformSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/platform/settings`)
      .then(r => setPlatformSettings(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (zipCode.length < 4) { setSurge(null); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/zip/${zipCode}/demand`);
        setSurge(res.data);
      } catch (e) {
        setSurge(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [zipCode]);

  // Auto-reset endTime if it's no longer valid
  useEffect(() => {
    if (endTime && startTime && endTime <= startTime) {
      setEndTime('');
    }
  }, [startTime, endTime]);

  // Fetch the published zip rate (for live estimated-price preview)
  const [zipRate, setZipRate] = useState(null);

  // Saved places — only loaded when logged in as a customer
  const [places, setPlaces] = useState([]);
  const [selectedPlaceId, setSelectedPlaceId] = useState('');

  useEffect(() => {
    if (!user || user.role !== 'customer') return;
    const token = localStorage.getItem('token');
    axios.get(`${API}/places`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const list = r.data || [];
        setPlaces(list);
        const defaultPlace = list.find(p => p.is_default) || list[0];
        if (defaultPlace) {
          setSelectedPlaceId(defaultPlace.id);
          setZipCode(defaultPlace.zip_code);
        }
      })
      .catch(() => {});
  }, [user]);

  const onPlaceChange = (id) => {
    setSelectedPlaceId(id);
    const p = places.find(x => x.id === id);
    if (p) setZipCode(p.zip_code);
  };

  useEffect(() => {
    if (zipCode.length !== 5) { setZipRate(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/zip-rates/${zipCode}`);
        setZipRate(r.data?.base_rate ?? null);
      } catch (_) {
        setZipRate(null);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [zipCode]);

  const durationHours = hoursBetween(startTime, endTime);
  const estimatedPrice = zipRate && durationHours > 0 ? zipRate * durationHours : null;

  const handleSearch = () => {
    if (!(zipCode && serviceDate && startTime && endTime)) return;
    let params = `zip=${zipCode}&date=${serviceDate}&time=${startTime}&end=${endTime}`;
    if (selectedPlaceId) params += `&place_id=${selectedPlaceId}`;
    // Cleaner list is gated — must be logged in to see availability
    if (!user) {
      navigate(`/auth?next=${encodeURIComponent('/search?' + params)}`);
      return;
    }
    navigate(`/search?${params}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} setUser={setUser} />
      
      {/* Pre-launch announcement banner */}
      {platformSettings && platformSettings.bookings_enabled === false && (
        <div className="bg-primary text-white text-center px-4 py-2.5 text-sm font-medium" data-testid="prelaunch-banner">
          <span className="opacity-90">We&apos;re onboarding cleaners first — customer bookings open soon. </span>
          <a href="/auth?role=cleaner" className="underline font-semibold hover:opacity-80">Are you a cleaner? Join now →</a>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center" style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1631510390389-c1e4fb20ff31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxjbGVhbiUyMGJyaWdodCUyMGxpdmluZyUyMHJvb20lMjBtb2Rlcm58ZW58MHx8fHwxNzgxNzAyNTgwfDA&ixlib=rb-4.1.0&q=85)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="hero-overlay absolute inset-0"></div>
        <div className="relative z-10 w-full px-6 md:px-12 lg:px-16">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none text-text-primary mb-6">
                {t('home.heroTitle', 'Book your perfect clean.')}
              </h1>
              <p className="text-lg sm:text-xl text-text-secondary leading-relaxed mb-8">
                {t('home.heroSubtitle', 'Browse trusted professionals in your area. Quick, reliable, and transparent.')}
              </p>
            </div>
            
            {/* Booking Widget */}
            <div className="lg:col-span-5 lg:col-start-8">
              <div className="bg-surface rounded-2xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.08)] border border-border" data-testid="booking-widget">
                <h3 className="font-heading text-2xl font-semibold text-text-primary mb-6">{t('nav.findCleaner', 'Find Your Cleaner')}</h3>
                
                <div className="space-y-4">
                  {/* Address / zip selector */}
                  {user && user.role === 'customer' && places.length > 0 ? (
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">Service address</label>
                      <select
                        value={selectedPlaceId}
                        onChange={(e) => onPlaceChange(e.target.value)}
                        className="input-field w-full"
                        data-testid="search-place-select"
                      >
                        {places.map(p => (
                          <option key={p.id} value={p.id}>
                            {`${p.label || 'My place'} — ${p.street_address}${p.unit ? `, ${p.unit}` : ''}, ${p.city || ''} ${p.zip_code}${p.is_default ? ' · default' : ''}`}
                          </option>
                        ))}
                      </select>
                      <a href="/dashboard/customer?tab=places" className="text-xs text-primary hover:underline mt-1.5 inline-block">
                        Manage addresses →
                      </a>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">{t('home.searchZipPlaceholder', 'Zip Code')}</label>
                      <div className="relative">
                        <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
                        <input
                          type="text"
                          placeholder={t('home.searchZipPlaceholder', 'Enter zip code')}
                          value={zipCode}
                          onChange={(e) => setZipCode(e.target.value)}
                          className="input-field with-icon w-full"
                          data-testid="search-zip-input"
                        />
                      </div>
                      {user && user.role === 'customer' && places.length === 0 && (
                        <a href="/dashboard/customer?tab=places" className="text-xs text-primary hover:underline mt-1.5 inline-block">
                          Add an address to your profile →
                        </a>
                      )}
                      {surge && surge.multiplier > 1 && (
                        <div className={`mt-2 flex items-center gap-2 text-xs font-medium ${
                          surge.level === 'peak' ? 'text-red-600' :
                          surge.level === 'high' ? 'text-orange-600' :
                          'text-yellow-700'
                        }`} data-testid="home-surge-indicator">
                          <Flame className="w-4 h-4" />
                          {surge.label} (+{surge.premium_percent}%)
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">{t('home.searchDate', 'Service Date')}</label>
                    <DatePicker
                      value={serviceDate}
                      onChange={setServiceDate}
                      placeholder={t('home.searchDatePlaceholder', 'Pick a date')}
                      dataTestId="search-date-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Service Time</label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="relative">
                        <Clock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
                        <select
                          value={startTime}
                          onChange={(e) => setStartTime(e.target.value)}
                          className="input-field with-icon w-full"
                          data-testid="search-start-time"
                        >
                          <option value="">{t('home.searchStart', 'From')}</option>
                          {ALL_TIME_SLOTS.map(t => (
                            <option key={t} value={t}>{formatTime12(t)}</option>
                          ))}
                        </select>
                      </div>
                      <div className="relative">
                        <Clock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
                        <select
                          value={endTime}
                          onChange={(e) => setEndTime(e.target.value)}
                          className="input-field with-icon w-full"
                          disabled={!startTime}
                          data-testid="search-end-time"
                        >
                          <option value="">{t('home.searchEnd', 'To')}</option>
                          {endTimesFor(startTime).map(t => (
                            <option key={t} value={t}>{formatTime12(t)}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {durationHours > 0 && (
                      <p className="text-xs text-text-secondary mt-2">
                        <span className="font-semibold text-primary">{durationHours} {durationHours === 1 ? 'h' : 'h'}</span>
                      </p>
                    )}
                  </div>

                  {/* Estimated price preview */}
                  {estimatedPrice !== null && (
                    <div className="mt-5 p-4 rounded-2xl bg-primary-light/40 border border-primary/20" data-testid="estimated-price">
                      <div className="flex items-baseline justify-between">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-text-secondary font-medium">Estimated price</p>
                          <p className="text-3xl font-heading font-bold text-primary mt-0.5">
                            ${estimatedPrice.toFixed(2)}
                          </p>
                          <p className="text-xs text-text-secondary mt-1">
                            ${zipRate.toFixed(2)}/hr × {durationHours} hr · before tips &amp; supplies
                          </p>
                        </div>
                        {!user && (
                          <p className="text-xs text-text-secondary italic max-w-[160px] text-right">
                            Sign in to see available cleaners
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  {zipCode.length === 5 && zipRate === null && (
                    <div className="mt-5 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs" data-testid="no-zip-rate">
                      We haven&apos;t set a rate for <strong>{zipCode}</strong> yet. We&apos;ll show you what cleaners in nearby areas charge after you sign in.
                    </div>
                  )}

                  <button
                    onClick={handleSearch}
                    className="btn-primary w-full mt-5"
                    data-testid="find-cleaners-button"
                  >
                    {user ? t('home.searchCTA', 'Find Cleaners') : 'Sign in to find cleaners'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-24 px-6 md:px-12 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto mb-6">
                <Star className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-text-primary mb-3">{t('home.feature1Title', 'Vetted Professionals')}</h3>
              <p className="text-text-secondary">{t('home.feature1Body', 'All cleaners are verified with background checks and customer reviews.')}</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto mb-6">
                <Calendar className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-text-primary mb-3">{t('home.feature2Title', 'Instant Booking')}</h3>
              <p className="text-text-secondary">{t('home.feature2Body', 'Book your cleaning service in minutes with real-time availability.')}</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto mb-6">
                <MapPin className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-text-primary mb-3">{t('home.feature4Title', 'Secure Payments')}</h3>
              <p className="text-text-secondary">{t('home.feature4Body', 'Safe and secure payment processing with transparent pricing.')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-text-primary text-white py-20 px-6 md:px-12 lg:px-16">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="font-heading text-3xl font-bold mb-4">{t('home.ctaTitle', 'Ready to get started?')}</h2>
          <p className="text-gray-300 mb-8">{t('home.ctaSubtitle', 'Join thousands of satisfied customers and cleaners.')}</p>
          <div className="flex gap-4 justify-center">
            <button onClick={() => navigate('/auth')} className="bg-white text-primary font-semibold px-8 py-3 rounded-full hover:bg-gray-100 transition-all duration-300">
              {t('home.ctaFind', 'Find a Cleaner')}
            </button>
            <button onClick={() => navigate('/auth')} className="border-2 border-white text-white font-semibold px-8 py-3 rounded-full hover:bg-white hover:text-primary transition-all duration-300">
              {t('home.ctaJoin', 'Become a Cleaner')}
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
